/**
 * Supabase Client
 *
 * Initializes Supabase client for Realtime Broadcast.
 * Used for phone ↔ reader pairing communication.
 *
 * No database operations - Broadcast only.
 */

import { createClient, type SupabaseClient, type RealtimeChannel } from '@supabase/supabase-js';
import { getChannelName } from './sessionCode';

/**
 * Supabase configuration from environment.
 * In Vite, these are exposed via import.meta.env.
 */
interface SupabaseConfig {
  url: string;
  anonKey: string;
}

/**
 * Broadcast event name used for all pairing messages.
 */
export const BROADCAST_EVENT = 'cmd';

/**
 * Get Supabase configuration from environment.
 *
 * @returns Configuration object or null if not configured
 */
export function getSupabaseConfig(): SupabaseConfig | null {
  // Vite exposes env vars prefixed with VITE_
  const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

  if (!url || !anonKey) {
    return null;
  }

  return { url, anonKey };
}

/**
 * Check if Supabase is configured.
 *
 * @returns true if environment variables are set
 */
export function isSupabaseConfigured(): boolean {
  return getSupabaseConfig() !== null;
}

// Singleton client instance
let client: SupabaseClient | null = null;

/**
 * Get or create the Supabase client singleton.
 *
 * @returns Supabase client instance
 * @throws Error if Supabase is not configured
 */
export function getSupabaseClient(): SupabaseClient {
  if (client) {
    return client;
  }

  const config = getSupabaseConfig();
  if (!config) {
    throw new Error(
      'Supabase not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY environment variables.'
    );
  }

  client = createClient(config.url, config.anonKey, {
    realtime: {
      params: {
        eventsPerSecond: 10,
      },
    },
  });

  return client;
}

/**
 * Create a broadcast channel for a pairing session.
 *
 * @param sessionCode - The session code
 * @returns RealtimeChannel configured for broadcast
 */
export function createPairingChannel(sessionCode: string): RealtimeChannel {
  const supabase = getSupabaseClient();
  const channelName = getChannelName(sessionCode);

  return supabase.channel(channelName, {
    config: {
      broadcast: {
        // Don't require ack from server (faster, fire-and-forget to server)
        ack: false,
        // Receive own messages (useful for debugging, but we filter them)
        self: false,
      },
    },
  });
}

/**
 * Broadcast a message on a channel.
 *
 * @param channel - The realtime channel
 * @param payload - Message payload to broadcast
 * @returns Promise that resolves when message is sent
 */
export async function broadcastMessage(
  channel: RealtimeChannel,
  payload: Record<string, unknown>
): Promise<void> {
  await channel.send({
    type: 'broadcast',
    event: BROADCAST_EVENT,
    payload,
  });
}

/**
 * Subscribe to broadcast messages on a channel.
 *
 * @param channel - The realtime channel
 * @param callback - Function called with each received message payload
 * @returns The channel (for chaining)
 */
export function onBroadcastMessage(
  channel: RealtimeChannel,
  callback: (payload: Record<string, unknown>) => void
): RealtimeChannel {
  return channel.on('broadcast', { event: BROADCAST_EVENT }, ({ payload }) => {
    if (payload) {
      callback(payload as Record<string, unknown>);
    }
  });
}

/**
 * Subscribe to channel and wait for subscription to be ready.
 *
 * @param channel - The realtime channel
 * @param timeoutMs - Timeout in milliseconds (default: 10000)
 * @returns Promise that resolves when subscribed, rejects on timeout/error
 */
export function subscribeChannel(
  channel: RealtimeChannel,
  timeoutMs: number = 10000
): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error('Channel subscription timeout'));
    }, timeoutMs);

    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        clearTimeout(timeout);
        resolve();
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        clearTimeout(timeout);
        reject(new Error(`Channel subscription failed: ${status}`));
      }
    });
  });
}

/**
 * Unsubscribe and remove a channel.
 *
 * @param channel - The realtime channel to remove
 */
export async function removeChannel(channel: RealtimeChannel): Promise<void> {
  const supabase = getSupabaseClient();
  await supabase.removeChannel(channel);
}

/**
 * Clean up all channels and reset client.
 * Useful for testing or cleanup on unmount.
 */
export async function cleanup(): Promise<void> {
  if (client) {
    await client.removeAllChannels();
    client = null;
  }
}
